import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { animate, style, transition, trigger } from '@angular/animations';
import { LicenseService } from '@core/services';
import { NotificationService } from '@core/services';
import { AuthenticationService } from '@core/services';
import { environment } from '../../../../environments/environment';

interface OrgMember {
  user_id: string;
  role: string;
  status: string;
  joined_at: string;
  profiles?: {
    display_name: string | null;
  };
  email?: string;
  isInvite?: boolean;
}

interface OrgLicense {
  license_type: string;
  status: string;
  start_at: string;
  end_at: string | null;
  seat_limit: number | null;
  assigned_seats: number;
}

@Component({
  selector: 'app-org-members',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './org-members.component.html',
  styleUrls: ['./org-members.component.scss'],
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(8px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
    trigger('rowIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(-8px)' }),
        animate('250ms ease-out', style({ opacity: 1, transform: 'translateX(0)' })),
      ]),
    ]),
  ],
})
export class OrgMembersComponent implements OnInit {
  members: OrgMember[] = [];
  license: OrgLicense | null = null;
  isLoading = true;
  isSendingInvite = false;
  isRemoving: string | null = null;
  isUpdatingRole: string | null = null;

  inviteEmail = '';
  showInviteForm = false;
  inviteError = '';

  // Email Routing Settings
  summaryEmailRecipients = '';
  summaryEmailTargetType: 'all' | 'selected' = 'all';
  summaryEmailTargetUsers: string[] = [];
  isSavingConfig = false;
  configError = '';

  organizationId: string | null = null;
  currentUserId: string | null = null;

  constructor(
    private readonly _licenseService: LicenseService,
    private readonly _notificationService: NotificationService,
    private readonly _auth: AuthenticationService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.organizationId = this._licenseService.organizationId;
    const userDetail = this._auth.getUserDetail();
    this.currentUserId = userDetail?.user?.id ?? null;
    await this._loadAll();
  }

  get seatsLabel(): string {
    if (!this.license) return '';
    const used = this.license.assigned_seats;
    const total = this.license.seat_limit ?? '∞';
    return `${used} / ${total} seats`;
  }

  get seatsFull(): boolean {
    if (!this.license?.seat_limit) return false;
    return this.license.assigned_seats >= this.license.seat_limit;
  }

  getMemberName(member: OrgMember): string {
    return member.profiles?.display_name || member.email || member.user_id.slice(0, 8);
  }

  getRoleLabel(role: string): string {
    return role === 'organizational_admin' ? 'Admin' : 'Member';
  }

  isCurrentUser(member: OrgMember): boolean {
    return member.user_id === this.currentUserId;
  }

  async sendInvite(): Promise<void> {
    const email = this.inviteEmail.trim().toLowerCase();
    if (!email) {
      this.inviteError = 'Please enter an email address';
      return;
    }
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      this.inviteError = 'Please enter a valid email address';
      return;
    }
    if (this.seatsFull) {
      this._notificationService.notify('No seats available. Contact your admin to increase seat limit.');
      return;
    }

    this.inviteError = '';
    this.isSendingInvite = true;
    try {
      await this._callFunction('invite-org-member', {
        organization_id: this.organizationId,
        email,
      });
      this._notificationService.notify(`Invite sent to ${email}`);
      this.inviteEmail = '';
      this.showInviteForm = false;
      await this._loadAll();
    } catch (err: any) {
      console.error('[OrgMembers] invite error:', err);
      this.inviteError = err?.message ?? 'Failed to send invite';
    } finally {
      this.isSendingInvite = false;
    }
  }

  async removeMember(member: OrgMember): Promise<void> {
    if (this.isCurrentUser(member)) return;
    const actionLabel = member.isInvite ? 'Revoke invite for' : 'Remove';
    if (!confirm(`${actionLabel} ${this.getMemberName(member)}?`)) return;

    this.isRemoving = member.user_id;
    try {
      await this._callFunction('remove-org-member', {
        organization_id: this.organizationId,
        user_id: member.user_id,
        email: member.email,
        is_invite: member.isInvite,
      });
      this._notificationService.notify(member.isInvite ? 'Invite revoked' : 'Member removed successfully');
      await this._loadAll();
    } catch (err: any) {
      console.error('[OrgMembers] remove error:', err);
      this._notificationService.notifyError(err?.message ?? 'Failed to remove member');
    } finally {
      this.isRemoving = null;
    }
  }

  async toggleRole(member: OrgMember): Promise<void> {
    if (this.isCurrentUser(member)) return;
    const newRole = member.role === 'organizational_admin' ? 'organization_member' : 'organizational_admin';
    const actionLabel = newRole === 'organizational_admin' ? 'promote to Admin' : 'demote to Member';
    if (!confirm(`${actionLabel} ${this.getMemberName(member)}?`)) return;

    this.isUpdatingRole = member.user_id;
    try {
      await this._callFunction('update-org-member-role', {
        organization_id: this.organizationId,
        user_id: member.user_id,
        new_role: newRole,
      });
      member.role = newRole;
      this._notificationService.notify('Role updated');
    } catch (err: any) {
      console.error('[OrgMembers] role update error:', err);
      this._notificationService.notifyError(err?.message ?? 'Failed to update role');
    } finally {
      this.isUpdatingRole = null;
    }
  }

  formatDate(iso: string | null): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  private async _loadAll(): Promise<void> {
    this.isLoading = true;
    try {
      const [membersData, licenseData] = await Promise.all([
        this._loadMembers(),
        this._loadLicense(),
        this._loadConfig(),
      ]);
      this.members = membersData;
      this.license = licenseData;
    } catch (err) {
      console.error('[OrgMembers] load error:', err);
      this._notificationService.notifyError('Failed to load organization data');
    } finally {
      this.isLoading = false;
    }
  }

  private async _loadMembers(): Promise<OrgMember[]> {
    if (!this.organizationId) return [];
    const token = this._auth.getUserDetail()?.access_token;
    
    const [resMembers, resInvites] = await Promise.all([
      fetch(
        `${environment.apiUrl}/rest/v1/rpc/get_org_members_with_email`,
        { 
          method: 'POST',
          headers: { 
            Authorization: `Bearer ${token}`, 
            apikey: environment.apiKey,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ p_org_id: this.organizationId })
        }
      ),
      fetch(
        `${environment.apiUrl}/rest/v1/organization_invites?organization_id=eq.${this.organizationId}&accepted=eq.false&expires_at=gt.${new Date().toISOString()}&select=email,role,created_at&order=created_at.asc`,
        { headers: { Authorization: `Bearer ${token}`, apikey: environment.apiKey } }
      )
    ]);

    if (!resMembers.ok) throw new Error('Failed to load members');
    
    const rawMembers = await resMembers.json();
    const members = rawMembers.map((m: any) => ({
      ...m,
      profiles: { display_name: m.display_name }
    }));
    let mappedInvites: OrgMember[] = [];
    
    if (resInvites.ok) {
      const invites = await resInvites.json();
      mappedInvites = invites.map((inv: any) => ({
        user_id: inv.email, // Use email as unique key for UI
        role: inv.role,
        status: 'pending',
        joined_at: inv.created_at,
        email: inv.email,
        isInvite: true
      }));
    }

    return [...members, ...mappedInvites];
  }

  private async _loadLicense(): Promise<OrgLicense | null> {
    if (!this.organizationId) return null;
    const token = this._auth.getUserDetail()?.access_token;
    const res = await fetch(
      `${environment.apiUrl}/rest/v1/licenses?organization_id=eq.${this.organizationId}&select=id,license_type,status,start_at,end_at,seat_limit&limit=1`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          apikey: environment.apiKey,
        },  
      }
    );
    if (!res.ok) throw new Error('Failed to load license');
    const rows = await res.json();
    const lic = rows?.[0];
    if (!lic) return null;

    // Count assigned seats
    const seatsRes = await fetch(
      `${environment.apiUrl}/rest/v1/license_assignments?license_id=eq.${lic.id}&select=count`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          apikey: environment.apiKey,
          Prefer: 'count=exact',
        },
      }
    );
    const assignedCount = parseInt(seatsRes.headers.get('content-range')?.split('/')[1] ?? '0', 10);

    // Count pending invites (not yet accepted and not expired)
    const invitesRes = await fetch(
      `${environment.apiUrl}/rest/v1/organization_invites?organization_id=eq.${this.organizationId}&accepted=eq.false&expires_at=gt.${new Date().toISOString()}&select=count`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          apikey: environment.apiKey,
          Prefer: 'count=exact',
        },
      }
    );
    const invitesCount = parseInt(invitesRes.headers.get('content-range')?.split('/')[1] ?? '0', 10);

    return { ...lic, assigned_seats: assignedCount + invitesCount };
  }

  private async _callFunction(name: string, body: Record<string, unknown>): Promise<any> {
    const token = this._auth.getUserDetail()?.access_token;
    const res = await fetch(`${environment.apiUrl}/functions/v1/${name}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        apikey: environment.apiKey,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.error ?? data?.message ?? `HTTP ${res.status}`);
    return data;
  }

  private async _loadConfig(): Promise<void> {
    if (!this.organizationId) return;
    const token = this._auth.getUserDetail()?.access_token;
    try {
      const res = await fetch(
        `${environment.apiUrl}/rest/v1/organizations?id=eq.${this.organizationId}&select=summary_email_recipients,summary_email_target_type,summary_email_target_users`,
        { headers: { Authorization: `Bearer ${token}`, apikey: environment.apiKey } }
      );
      if (!res.ok) return;
      const rows = await res.json();
      if (rows && rows[0]) {
        this.summaryEmailRecipients = (rows[0].summary_email_recipients || []).join(', ');
        this.summaryEmailTargetType = rows[0].summary_email_target_type || 'all';
        this.summaryEmailTargetUsers = rows[0].summary_email_target_users || [];
      }
    } catch (e) {
      console.error('Failed to load routing config', e);
    }
  }

  async saveConfig(): Promise<void> {
    if (!this.organizationId) return;
    this.isSavingConfig = true;
    this.configError = '';

    const recipientsArray = this.summaryEmailRecipients
      .split(',')
      .map(e => e.trim())
      .filter(e => e.length > 0);

    const token = this._auth.getUserDetail()?.access_token;
    try {
      const res = await fetch(`${environment.apiUrl}/rest/v1/organizations?id=eq.${this.organizationId}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          apikey: environment.apiKey,
          'Content-Type': 'application/json',
          Prefer: 'return=minimal'
        },
        body: JSON.stringify({
          summary_email_recipients: recipientsArray,
          summary_email_target_type: this.summaryEmailTargetType,
          summary_email_target_users: this.summaryEmailTargetType === 'all' ? [] : this.summaryEmailTargetUsers
        })
      });

      if (!res.ok) throw new Error('Failed to update routing configuration');
      this._notificationService.notify('Email routing configuration saved');
    } catch (err: any) {
      this.configError = err.message || 'Error saving configuration';
    } finally {
      this.isSavingConfig = false;
    }
  }

  toggleTargetUser(userId: string) {
    const idx = this.summaryEmailTargetUsers.indexOf(userId);
    if (idx >= 0) {
      this.summaryEmailTargetUsers.splice(idx, 1);
    } else {
      this.summaryEmailTargetUsers.push(userId);
    }
  }
}
